import api from './api';

export interface FigmaInstance {
  id: number;
  client_id: number;
  instance_name: string;
  team_id?: string;
  created_at: string;
  updated_at: string;
}

export interface FigmaFile {
  id: number;
  instance_id: number;
  project_id: number;
  file_key: string;
  file_name: string;
  last_modified?: string;
  thumbnail_url?: string;
  created_at: string;
  updated_at: string;
  project_name?: string;
  embedUrl?: string;
  unresolvedComments?: number;
}

export interface FigmaFileVersion {
  id: number;
  file_id: number;
  version_id: string;
  label?: string;
  description?: string;
  created_at: string;
  created_by?: string;
  is_current: boolean;
}

export interface FigmaComment {
  id: number;
  file_id: number;
  task_id?: number;
  figma_comment_id: string;
  parent_comment_id?: string;
  user_name: string;
  user_handle?: string;
  message: string;
  node_id?: string;
  frame_name?: string;
  created_at: string;
  resolved: boolean;
  resolved_at?: string;
}

export interface FigmaFileDetails extends FigmaFile {
  versions: FigmaFileVersion[];
  comments: FigmaComment[];
  instance_name: string;
  client_id: number;
}

export interface CreateFigmaInstanceRequest {
  clientId: number;
  instanceName: string;
  accessToken: string;
  teamId?: string;
}

export interface SyncFigmaFileRequest {
  fileKey: string;
  projectId: number;
}

export interface LinkFileToTaskRequest {
  taskId: number;
  fileId: number;
}

export interface PostCommentRequest {
  message: string;
  nodeId?: string;
}

/**
 * Get all Figma instances for a client
 */
export const getFigmaInstances = async (clientId: number, token: string): Promise<FigmaInstance[]> => {
  return api.get<FigmaInstance[]>(`/api/clients/${clientId}/figma/instances`, token);
};

/**
 * Create a new Figma instance connection
 */
export const createFigmaInstance = async (
  data: CreateFigmaInstanceRequest,
  token: string
): Promise<{ id: number; message: string }> => {
  return api.post<{ id: number; message: string }>('/api/figma/instances', data, token);
};

/**
 * Get all files for a Figma instance
 */
export const getFigmaFiles = async (instanceId: number, token: string): Promise<FigmaFile[]> => {
  return api.get<FigmaFile[]>(`/api/figma/instances/${instanceId}/files`, token);
};

/**
 * Sync a Figma file
 */
export const syncFigmaFile = async (
  instanceId: number,
  data: SyncFigmaFileRequest,
  token: string
): Promise<{ message: string; fileId: number; fileName: string; versionsCount: number; commentsCount: number }> => {
  return api.post<{ message: string; fileId: number; fileName: string; versionsCount: number; commentsCount: number }>(
    `/api/figma/instances/${instanceId}/sync`,
    data,
    token
  );
};

/**
 * Get file details including versions and comments
 */
export const getFigmaFileDetails = async (fileId: number, token: string): Promise<FigmaFileDetails> => {
  return api.get<FigmaFileDetails>(`/api/figma/files/${fileId}`, token);
};

/**
 * Link Figma file to task
 */
export const linkFigmaFileToTask = async (
  fileId: number,
  data: LinkFileToTaskRequest,
  token: string
): Promise<{ message: string }> => {
  return api.post<{ message: string }>(`/api/figma/files/${fileId}/link-task`, data, token);
};

/**
 * Post comment to Figma
 */
export const postFigmaComment = async (
  fileId: number,
  data: PostCommentRequest,
  token: string
): Promise<{ message: string; comment: any }> => {
  return api.post<{ message: string; comment: any }>(`/api/figma/files/${fileId}/comments`, data, token);
};

/**
 * Get task's Figma file
 */
export const getTaskFigmaFile = async (taskId: number, token: string): Promise<{ figmaFile: FigmaFile | null }> => {
  return api.get<{ figmaFile: FigmaFile | null }>(`/api/tasks/${taskId}/figma`, token);
};

/**
 * Extract file key from Figma URL
 */
export const extractFileKeyFromUrl = (url: string): string | null => {
  // Figma URL patterns:
  // https://www.figma.com/file/{file_key}/{file_name}
  // https://www.figma.com/design/{file_key}/{file_name}
  const match = url.match(/figma\.com\/(file|design)\/([a-zA-Z0-9]+)/);
  return match ? match[2] : null;
};

/**
 * Get Figma file URL from file key
 */
export const getFigmaFileUrl = (fileKey: string): string => {
  return `https://www.figma.com/file/${fileKey}`;
};

/**
 * Format Figma embed URL
 */
export const getFigmaEmbedUrl = (fileKey: string, nodeId?: string): string => {
  const baseUrl = `https://www.figma.com/embed?embed_host=creative-agency&url=https://www.figma.com/file/${fileKey}`;
  return nodeId ? `${baseUrl}?node-id=${nodeId}` : baseUrl;
};
